import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { PaymentEvent } from 'tio-data/types/tuition-assistance';

/**
 * Plain-object fixtures for the TAS course-management ("manage") screen, one per
 * derivation branch in `deriveAttention`. Used by the dev-only manage gallery
 * route and by the ManagePage integration test, so both stay in sync.
 *
 * These are NOT live WarpDrive records — they expose only the getters/relations
 * ManagePage + deriveAttention + NeedsYourAttention read, then cast to the model
 * type. Money is in cents (the screen converts to dollars).
 */
export interface ManageFixture {
  key: string;
  label: string;
  application: TasApplicationModel;
}

interface FixtureOverrides {
  displayName?: string;
  state?: string;
  reserved?: number;
  used?: number;
  isEvidenceRequired?: boolean;
  isMissingRequiredEvidence?: boolean;
  isApproved?: boolean;
  approvedTotal?: number;
  paidTotal?: number;
  shouldDisplayApprovedTotal?: boolean;
  shouldDisplayPaymentDetails?: boolean;
  paymentsHistory?: PaymentEvent[];
  receiptUploaded?: boolean;
  withComment?: boolean;
  isDirectPayProgram?: boolean;
}

const LOREM =
  'Thanks for submitting — your reviewer will reach out if anything else is needed for this request.';

function buildApplication(overrides: FixtureOverrides = {}): TasApplicationModel {
  const reserved = overrides.reserved ?? 0;
  const used = overrides.used ?? 0;
  return {
    displayName: overrides.displayName ?? 'Summer Term 2026',
    state: overrides.state ?? 'TAS.ApplicationState.ATTEND',
    amounts: { course_cash: { reserved, used } },
    courseCashAvailableBalance: reserved - used,
    isEvidenceRequired: overrides.isEvidenceRequired ?? false,
    isMissingRequiredEvidence: overrides.isMissingRequiredEvidence ?? false,
    isApproved: overrides.isApproved ?? false,
    approvedTotal: overrides.approvedTotal ?? 134600,
    paidTotal: overrides.paidTotal ?? 0,
    requestedTotal: 139600,
    shouldDisplayApprovedTotal: overrides.shouldDisplayApprovedTotal ?? true,
    shouldDisplayPaymentDetails: overrides.shouldDisplayPaymentDetails ?? false,
    paymentsHistory: overrides.paymentsHistory ?? [],
    isCreditBased: false,
    totalCredits: 0,
    receiptUploaded: overrides.receiptUploaded ?? false,
    applicationSubmittedDate: '2026-06-01',
    coursesBeginDate: '2026-06-15',
    fields: { COURSES_END_DATE: { values: ['2026-08-15'] } },
    activeCourses: [
      { displayName: 'Intro to Nursing', courseTuition: 100000 },
      { displayName: 'Pharmacology I', courseTuition: 39600 },
    ],
    comments: overrides.withComment
      ? [{ comment: LOREM, isTasApproval: false, createdAt: '2026-06-10' }]
      : [],
    pcsCards: reserved > 0 ? [{ isActive: true, providerData: { last4: '8882' } }] : [],
    tasProgramInstance: {
      fields: { SCHOOL_INSTITUTION_NAME: { values: ['Healthy Hair Academy'] } },
      tasProgramTemplate: {
        programName: "Karen's Advocate Health Program",
        isDirectPayProgram: overrides.isDirectPayProgram ?? false,
      },
    },
  } as unknown as TasApplicationModel;
}

export const MANAGE_FIXTURES: ManageFixture[] = [
  {
    key: 'submitted',
    label: 'Submitted / under review (anchor)',
    application: buildApplication({
      state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      shouldDisplayApprovedTotal: false,
    }),
  },
  {
    key: 'all-caught-up',
    label: 'All caught up (empty state)',
    application: buildApplication({ state: 'TAS.ApplicationState.FULFILLED', withComment: true }),
  },
  {
    key: 'panel-hidden',
    label: 'Panel suppressed (nothing to surface)',
    application: buildApplication({
      state: 'TAS.ApplicationState.ATTEND',
      shouldDisplayApprovedTotal: false,
    }),
  },
  {
    key: 'evidence-only',
    label: 'Submit evidence (no CourseCash)',
    application: buildApplication({
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
      withComment: true,
    }),
  },
  {
    key: 'course-cash-available',
    label: 'CourseCash available + evidence',
    application: buildApplication({
      reserved: 101800,
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
      isDirectPayProgram: true,
    }),
  },
  {
    key: 'approved',
    label: 'Approved (anchor) + CourseCash + evidence',
    application: buildApplication({
      state: 'TAS.ApplicationState.COURSES_APPROVED',
      isApproved: true,
      reserved: 101800,
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
      isDirectPayProgram: true,
      withComment: true,
    }),
  },
  {
    key: 'course-cash-used',
    label: 'CourseCash used → receipt + evidence (used anchor)',
    application: buildApplication({
      reserved: 101800,
      used: 101800,
      paidTotal: 101800,
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
      shouldDisplayPaymentDetails: true,
      isDirectPayProgram: true,
      paymentsHistory: [
        {
          amount: 101800,
          approverName: 'Karen Advocate',
          assistanceType: 'CARD_CAPTURED',
          date: '2026-06-20',
          from: 'TAS.ApplicationState.COURSES_APPROVED',
          logType: 'assistance',
          to: 'TAS.ApplicationState.ATTEND',
        },
        {
          amount: 101800,
          approverName: 'Karen Advocate',
          assistanceType: 'CARD_AUTHORIZED',
          date: '2026-06-16',
          from: 'TAS.ApplicationState.COURSES_APPROVED',
          logType: 'assistance',
          to: 'TAS.ApplicationState.COURSES_APPROVED',
        },
      ],
    }),
  },
  {
    key: 'receipt-uploaded',
    label: 'CourseCash used, receipt in → evidence remaining',
    application: buildApplication({
      reserved: 101800,
      used: 101800,
      paidTotal: 101800,
      receiptUploaded: true,
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
      shouldDisplayPaymentDetails: true,
      isDirectPayProgram: true,
      paymentsHistory: [
        {
          amount: 101800,
          approverName: 'Karen Advocate',
          assistanceType: 'CARD_CAPTURED',
          date: '2026-06-20',
          from: 'TAS.ApplicationState.COURSES_APPROVED',
          logType: 'assistance',
          to: 'TAS.ApplicationState.ATTEND',
        },
        {
          amount: 101800,
          approverName: 'Karen Advocate',
          assistanceType: 'CARD_AUTHORIZED',
          date: '2026-06-16',
          from: 'TAS.ApplicationState.COURSES_APPROVED',
          logType: 'assistance',
          to: 'TAS.ApplicationState.COURSES_APPROVED',
        },
      ],
    }),
  },
];
